import { Button } from 'antd';
import { useMemo } from 'react';
import type { FC } from 'react';

import './Features.scss';

type Feature = {
  priority?: number;
  id: number;
  name: string;
  disabled?: boolean;
};

type Props = {
  value?: number[];
  onChange?: (newValue: number[]) => void;
  featuresByCategory: Record<string, Feature[]>;
  mode?: string;
  getDisabledFeatureIds?: (selectedFeatureIds: number[]) => number[];
};

const EMPTY_FEATURE_IDS: number[] = [];

export const FeaturesComponent: FC<Props> = ({
  value,
  onChange,
  featuresByCategory,
  mode = 'edit',
  getDisabledFeatureIds,
}) => {
  const selectedFeatureIds = value ?? EMPTY_FEATURE_IDS;
  const disabledFeatureIds = useMemo(
    () => getDisabledFeatureIds?.(selectedFeatureIds) ?? EMPTY_FEATURE_IDS,
    [getDisabledFeatureIds, selectedFeatureIds]
  );
  const disabledFeatureIdSet = useMemo(
    () => new Set(disabledFeatureIds),
    [disabledFeatureIds]
  );

  if (!onChange) {
    return null;
  }

  const handleChange = (feature: Feature, newValue: boolean) => {
    const isDisabled = feature.disabled || disabledFeatureIdSet.has(feature.id);

    if (newValue && isDisabled) {
      return;
    }

    if (newValue) {
      onChange([...selectedFeatureIds, feature.id]);
    } else {
      const index = selectedFeatureIds.indexOf(feature.id);

      if (index >= 0) {
        onChange([
          ...selectedFeatureIds.slice(0, index),
          ...selectedFeatureIds.slice(index + 1),
        ]);
      }
    }
  };

  const renderFeature = (feature: Feature) => {
    const checked = selectedFeatureIds.includes(feature.id);
    const isDisabled =
      feature.disabled || (!checked && disabledFeatureIdSet.has(feature.id));

    if (mode === 'view' && !checked) {
      return;
    }

    return (
      <Button
        key={`feature-${feature.id}`}
        className={`check-button ${checked ? 'check-button--checked' : ''}`}
        onClick={() => mode === 'edit' && handleChange(feature, !checked)}
        disabled={isDisabled}
      >
        {feature.name}
      </Button>
    );
  };

  return (
    <>
      {Object.keys(featuresByCategory).map((category, index) => {
        const features = featuresByCategory[category];
        const sortedFeatures = features.sort(
          (f1, f2) => (f1.priority || 0) - (f2.priority || 0)
        );

        return (
          <div key={`category-${index}`} className="feature-category">
            <div className="label">{category}</div>
            <div className="buttons">{sortedFeatures.map(renderFeature)}</div>
          </div>
        );
      })}
    </>
  );
};
